/**
 * Dashboard.tsx — Main overview page.
 *
 * Shows:
 *   - Platform-wide KPI cards (customers, scans, findings, avg risk score)
 *   - Machine Identity Risk (NHI) widget
 *   - Critical customers list with prominent risk gauges
 *   - 7-day risk prediction sparkline for the selected customer
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config/api';

import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GroupIcon from '@mui/icons-material/Group';
import KeyIcon from '@mui/icons-material/Key';
import PolicyIcon from '@mui/icons-material/Policy';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ShieldIcon from '@mui/icons-material/Shield';
import ScannerIcon from '@mui/icons-material/Scanner';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { Link, useNavigate } from 'react-router-dom';
import RiskScore from '../components/RiskScore';
import AccountIdBadge from '../components/AccountIdBadge';
import { useAuth } from '../context/AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PlatformSummary {
  total_customers: number;
  total_scans: number;
  total_findings: number;
  avg_risk_score: number;
  critical_customers: number;
}

interface Customer {
  id: string;
  name: string;
  organisation: string;
  tier: string;
}

interface CustomerRisk {
  customer_id: string;
  customer_name: string;
  latest_score: number | null;
  label: string | null;
  trend: 'rising' | 'stable' | 'falling';
  predicted_scores: number[];
  confidence: string;
  total_findings: number;
  critical_findings: number;
  high_findings: number;
}

interface NHISummary {
  total_identities: number;
  by_risk: Record<string, number>;
  by_provider: Record<string, number>;
  hardcoded_secrets: number;
  last_scan_at: string | null;
}

interface ScanUsage {
  today_manual: number;
  today_nhi: number;
  total_manual: number;
  total_nhi: number;
}

// ── KPI card ──────────────────────────────────────────────────────────────────

interface KpiCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accent?: string;
  onClick?: () => void;
}

function KpiCard({ title, value, icon, accent, onClick }: KpiCardProps) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        border: 1,
        borderColor: 'divider',
        height: '100%',
        ...(onClick && {
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            transform: 'translateY(-2px)',
            transition: 'all 0.2s',
          },
        }),
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} color={accent || 'text.primary'}>
              {value}
            </Typography>
          </Box>
          <Box sx={{ color: accent || 'primary.main', opacity: 0.8 }}>{icon}</Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// ── NHI widget ────────────────────────────────────────────────────────────────

interface NhiWidgetProps {
  customerId: string | null;
}

function NhiWidget({ customerId }: NhiWidgetProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<NHISummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customerId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get<NHISummary>(`${API_BASE}/v1/nhi/summary?customer_id=${customerId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [customerId]);

  const isEmpty =
    !data || (data.total_identities === 0 && data.hardcoded_secrets === 0);

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 4 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <KeyIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight={700}>
              Machine Identity Risk
            </Typography>
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate('/nhi')}
          >
            View Details
          </Button>
        </Box>

        {loading ? (
          <Box display="flex" alignItems="center" gap={2} py={1}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : !customerId ? (
          <Typography variant="body2" color="text.secondary">No data available.</Typography>
        ) : isEmpty ? (
          <Box display="flex" alignItems="center" gap={2} py={0.5}>
            <Typography variant="body2" color="text.secondary">
              No NHI scan run yet. Discover service accounts, API keys, and secrets across your cloud environments.
            </Typography>
            <Button size="small" variant="contained" onClick={() => navigate('/nhi')}>
              Run Scan
            </Button>
          </Box>
        ) : (
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <Box textAlign="center">
              <Typography variant="h4" fontWeight={700}>{data!.total_identities}</Typography>
              <Typography variant="caption" color="text.secondary">Identities</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
              {(data!.by_risk['critical'] ?? 0) > 0 && (
                <Chip
                  label={`${data!.by_risk['critical']} Critical`}
                  color="error"
                  size="small"
                />
              )}
              {(data!.by_risk['high'] ?? 0) > 0 && (
                <Chip
                  label={`${data!.by_risk['high']} High`}
                  color="warning"
                  size="small"
                />
              )}
              {(data!.by_risk['medium'] ?? 0) > 0 && (
                <Chip
                  label={`${data!.by_risk['medium']} Medium`}
                  color="info"
                  size="small"
                />
              )}
            </Stack>
            {data!.hardcoded_secrets > 0 && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box display="flex" alignItems="center" gap={0.5}>
                  <WarningAmberIcon color="error" fontSize="small" />
                  <Typography variant="body2" color="error.main" fontWeight={600}>
                    {data!.hardcoded_secrets} hardcoded secret{data!.hardcoded_secrets !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// ── Scan usage widget ─────────────────────────────────────────────────────────

interface ScanUsageWidgetProps {
  customerId: string | null;
}

function ScanUsageWidget({ customerId }: ScanUsageWidgetProps) {
  const [data, setData] = useState<ScanUsage | null>(null);

  useEffect(() => {
    if (!customerId) return;
    axios
      .get<ScanUsage>(`${API_BASE}/v1/scans/usage/${customerId}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [customerId]);

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 3 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <ScannerIcon sx={{ color: 'primary.main', fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={700}>Scan Usage</Typography>
        </Box>
        {!customerId || !data ? (
          <Typography variant="body2" color="text.secondary">No usage data available.</Typography>
        ) : (
          <Stack spacing={0.25}>
            <Typography variant="body2">
              Today: <strong>{data.today_manual}</strong> manual scan{data.today_manual !== 1 ? 's' : ''} &middot; <strong>{data.today_nhi}</strong> NHI scan{data.today_nhi !== 1 ? 's' : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All time: {data.total_manual} manual &middot; {data.total_nhi} NHI
            </Typography>
            <Typography variant="caption" color="text.disabled">
              Plan limit: Unlimited (Beta)
            </Typography>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// ── Mini sparkline for predicted scores ───────────────────────────────────────

function PredictionSparkline({ scores }: { scores: number[] }) {
  if (!scores.length) return null;
  const max = 100;
  const min = 0;
  const h = 40;
  const w = 120;
  const pad = 4;

  const points = scores
    .map((s, i) => {
      const x = pad + (i / (scores.length - 1)) * (w - 2 * pad);
      const y = h - pad - ((s - min) / (max - min)) * (h - 2 * pad);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline
        points={points}
        fill="none"
        stroke="#00b4d8"
        strokeWidth="1.8"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      {scores.map((s, i) => {
        const x = pad + (i / (scores.length - 1)) * (w - 2 * pad);
        const y = h - pad - ((s - min) / (max - min)) * (h - 2 * pad);
        return <circle key={i} cx={x} cy={y} r="2.5" fill="#00b4d8" />;
      })}
    </svg>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCustomer = user?.role === 'customer';

  const [summary, setSummary] = useState<PlatformSummary | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerRisks, setCustomerRisks] = useState<CustomerRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        if (isCustomer) {
          // Customer: fetch only their own scoped summary and risk card
          const summaryRes = await axios.get<PlatformSummary>(`${API_BASE}/v1/dashboard/summary`);
          setSummary(summaryRes.data);
          if (user?.customer_id) {
            try {
              const riskRes = await axios.get<CustomerRisk>(
                `${API_BASE}/v1/dashboard/customers/${user.customer_id}`
              );
              setCustomerRisks([riskRes.data]);
            } catch {
              // No scan data yet — leave risks empty
            }
          }
        } else {
          // Engineer / admin / super_admin: full platform view
          const [summaryRes, customersRes] = await Promise.all([
            axios.get<PlatformSummary>(`${API_BASE}/v1/dashboard/summary`),
            axios.get<Customer[]>(`${API_BASE}/v1/customers/`),
          ]);
          setSummary(summaryRes.data);
          setCustomers(customersRes.data);

          const riskResults = await Promise.allSettled(
            customersRes.data.map((c) =>
              axios.get<CustomerRisk>(`${API_BASE}/v1/dashboard/customers/${c.id}`)
            )
          );
          const risks = riskResults
            .filter((r): r is PromiseFulfilledResult<any> => r.status === 'fulfilled')
            .map((r) => r.value?.data ?? r.value);
          setCustomerRisks(risks);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isCustomer, user?.customer_id]); // eslint-disable-line

  // Sort customers by latest score descending (highest risk first)
  const sortedRisks = [...customerRisks].sort(
    (a, b) => (b.latest_score ?? 0) - (a.latest_score ?? 0)
  );

  // NHI widget customer id: own org for customers, first loaded customer for admins
  const nhiCustomerId = isCustomer
    ? (user?.customer_id ?? null)
    : (customers[0]?.id ?? null);

  // KPI cards differ by role
  const kpiCards: (KpiCardProps & { navigateTo: string })[] = isCustomer
    ? [
        {
          title: 'My Scans',
          value: loading ? '—' : summary?.total_scans ?? 0,
          icon: <ShieldIcon fontSize="large" />,
          navigateTo: '/scans',
        },
        {
          title: 'My Findings',
          value: loading ? '—' : summary?.total_findings ?? 0,
          icon: <PolicyIcon fontSize="large" />,
          navigateTo: '/alerts',
        },
        {
          title: 'My Risk Score',
          value: loading ? '—' : Math.round(summary?.avg_risk_score ?? 0),
          icon: <ReportProblemIcon fontSize="large" />,
          accent: (summary?.avg_risk_score ?? 0) > 75 ? '#ef233c' : undefined,
          navigateTo: '/scans',
        },
      ]
    : [
        {
          title: 'Partners',
          value: loading ? '—' : summary?.total_customers ?? 0,
          icon: <GroupIcon fontSize="large" />,
          navigateTo: '/admin/accounts',
        },
        {
          title: 'Total Scans',
          value: loading ? '—' : summary?.total_scans ?? 0,
          icon: <ShieldIcon fontSize="large" />,
          navigateTo: '/scans',
        },
        {
          title: 'Total Findings',
          value: loading ? '—' : summary?.total_findings ?? 0,
          icon: <PolicyIcon fontSize="large" />,
          navigateTo: '/alerts',
        },
        {
          title: 'Critical Partners',
          value: loading ? '—' : summary?.critical_customers ?? 0,
          icon: <ReportProblemIcon fontSize="large" />,
          accent: '#ef233c',
          navigateTo: '/alerts',
        },
      ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Dashboard</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Cloud Security & AI Monitoring Platform — real-time risk overview
      </Typography>

      {isCustomer && user?.customer_id && (
        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', px: 2.5, py: 1.5, mb: 3, display: 'flex', alignItems: 'center' }}>
          <AccountIdBadge />
        </Paper>
      )}

      {user?.account_status === 'paused' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Your account is paused</AlertTitle>
          Your data is safe and preserved for 30 days. After that it will be archived permanently.
          <Button size="small" component={Link} to="/billing" sx={{ ml: 2 }}>
            Reactivate now →
          </Button>
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── KPI cards ─────────────────────────────────────────────────────── */}
      <Grid container spacing={2} mb={4}>
        {kpiCards.map((kpi) => (
          <Grid item xs={12} sm={6} md={isCustomer ? 4 : 3} key={kpi.title}>
            <KpiCard
              title={kpi.title}
              value={kpi.value}
              icon={kpi.icon}
              accent={kpi.accent}
              onClick={() => navigate(kpi.navigateTo)}
            />
          </Grid>
        ))}
      </Grid>

      {/* ── NHI widget ────────────────────────────────────────────────────── */}
      {(!loading || isCustomer) && (
        <NhiWidget customerId={nhiCustomerId} />
      )}

      {/* ── Scan usage ────────────────────────────────────────────────────── */}
      {(!loading || isCustomer) && (
        <ScanUsageWidget customerId={nhiCustomerId} />
      )}

      {/* ── Average risk score ─────────────────────────────────────────────── */}
      {!loading && summary && (
        <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            {isCustomer ? 'My Current Risk Score' : 'Platform Average Risk Score'}
            <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 1 }}>
              (higher = more risk)
            </Typography>
          </Typography>
          <Box display="flex" alignItems="center" gap={4}>
            <RiskScore
              score={Math.round(summary.avg_risk_score)}
              label={
                summary.avg_risk_score <= 25 ? 'low'
                  : summary.avg_risk_score <= 50 ? 'medium'
                  : summary.avg_risk_score <= 75 ? 'high'
                  : 'critical'
              }
              size="large"
            />
            <Box>
              <Typography variant="body2" color="text.secondary">
                {isCustomer
                  ? 'Your current risk score based on your latest scan. Scores above 75 require immediate attention.'
                  : 'Average risk score across all customers. Scores above 75 require immediate attention.'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {/* ── Customer risk cards ────────────────────────────────────────────── */}
      <Typography variant="h6" gutterBottom>
        {isCustomer ? 'My Risk Overview' : 'Partner Risk Overview'}
      </Typography>
      {loading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((n) => (
            <Grid item xs={12} md={6} lg={4} key={n}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      ) : sortedRisks.length === 0 ? (
        <Alert severity="info">
          {isCustomer
            ? 'No scan data yet. Connect your cloud and run a scan to see results here.'
            : 'No partners found. Create partners and run scans to see data here.'}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {sortedRisks.map((risk) => (
            <Grid item xs={12} md={6} lg={4} key={risk.customer_id}>
              <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Typography variant="subtitle1" fontWeight={700} noWrap>
                        {risk.customer_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {risk.total_findings} findings · {risk.critical_findings} critical
                      </Typography>
                    </Box>
                    <RiskScore score={risk.latest_score} label={risk.label} trend={risk.trend} size="small" />
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="text.secondary">7-day prediction</Typography>
                      <Typography variant="caption" display="block" color="text.disabled">
                        Confidence: {risk.confidence}
                      </Typography>
                    </Box>
                    <PredictionSparkline scores={risk.predicted_scores} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
